import React, { useState, useMemo } from 'react';
import {
  TextField,
  InputAdornment,
  Popover,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import type { Icon as Interface } from '../../design/components';
import { FontIcon } from './FontIcon';

/** Lucide PascalCase icon names - translated to Material Icons via FontIcon */
const ICONS = [
  // Navigation & Actions
  'Home', 'Menu', 'X', 'Search', 'Settings', 'MoreVertical', 'MoreHorizontal',
  'Plus', 'Minus', 'Pencil', 'Trash2', 'Save', 'Check', 'CheckCircle',
  'RefreshCw', 'RefreshCcw', 'Undo', 'Redo',
  // Arrows
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'ChevronLeft', 'ChevronRight', 'ChevronDown', 'ChevronUp',
  // Status
  'Info', 'AlertTriangle', 'AlertCircle', 'HelpCircle', 'Clock', 'Ban', 'Flag',
  // People
  'User', 'UserPlus', 'Users', 'CircleUser', 'Smile',
  // Communication
  'Mail', 'Send', 'Phone', 'MessageCircle', 'MessageSquare', 'Bell',
  // Files
  'Folder', 'FolderOpen', 'File', 'FileText', 'StickyNote',
  'Paperclip', 'Link', 'Copy', 'Upload', 'Download',
  // Media
  'Image', 'Camera', 'Video', 'Mic', 'MicOff',
  'Play', 'Pause', 'Square',
  // Views
  'Eye', 'EyeOff', 'List', 'LayoutGrid', 'Grid3X3', 'Table',
  'LayoutDashboard', 'Filter', 'ArrowUpDown',
  'Maximize', 'Minimize', 'ZoomIn', 'ZoomOut',
  // Data
  'BarChart3', 'PieChart', 'TrendingUp', 'TrendingDown',
  // Calendar & Time
  'Calendar', 'CalendarDays', 'Timer', 'AlarmClock', 'History',
  // Location
  'MapPin', 'Map', 'Compass', 'Navigation', 'Globe', 'Languages',
  // Shopping
  'ShoppingCart', 'Store', 'Tag', 'CreditCard', 'Wallet',
  // Security
  'Shield', 'ShieldCheck', 'Lock', 'Unlock', 'Key',
  // Development
  'Code', 'Terminal', 'Braces', 'Database', 'Bug', 'Cloud',
  // Business
  'Building2', 'Briefcase', 'ClipboardList', 'ListChecks', 'Layers',
  // AI & Misc
  'Bot', 'Sparkles', 'Lightbulb', 'Hub', 'QrCode', 'Printer', 'Share',
  'GitBranch', 'Rocket',
];

export const Icon: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'Select an icon',
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  const filteredIcons = useMemo(() => {
    if (!search) return ICONS;
    const lower = search.toLowerCase();
    return ICONS.filter(name => name.toLowerCase().includes(lower));
  }, [search]);

  // Inline mode: render just the icon
  if (inline) {
    return value ? (
      <FontIcon name={value} style={{ display: hidden ? 'none' : undefined }} />
    ) : null;
  }

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!readonly && !disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleSelect = (iconName: string) => {
    setValue(iconName);
    handleClose();
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setValue('');
  };

  const popoverWidth = anchorEl?.offsetWidth || 360;

  return (
    <Box sx={{ display: hidden ? 'none' : undefined }}>
      <TextField
        id={name}
        name={name}
        label={name}
        value={value || ''}
        placeholder={placeholder}
        onClick={handleClick}
        error={!!error}
        disabled={disabled}
        required={required}
        fullWidth
        helperText={error || description}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                {value ? (
                  <FontIcon name={value} />
                ) : (
                  <FontIcon name="HelpCircle" color="rgba(0,0,0,0.38)" />
                )}
              </InputAdornment>
            ),
            endAdornment: value && !readonly && !disabled ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} aria-label="Clear icon">
                  <FontIcon name="X" size="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{
          cursor: readonly || disabled ? 'default' : 'pointer',
          '& .MuiInputBase-input': {
            cursor: readonly || disabled ? 'default' : 'pointer',
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { width: popoverWidth, maxHeight: 400 },
          },
        }}
      >
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search icons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FontIcon name="Search" size="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box
          sx={{
            p: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 60px)',
            gap: 0.5,
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          {filteredIcons.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 2 }}
            >
              No icons found
            </Typography>
          ) : (
            filteredIcons.map((iconName) => {
              const isSelected = value === iconName;
              return (
                <IconButton
                  key={iconName}
                  onClick={() => handleSelect(iconName)}
                  title={iconName}
                  sx={{
                    borderRadius: 1,
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    color: isSelected ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <FontIcon name={iconName} size="small" />
                </IconButton>
              );
            })
          )}
        </Box>
      </Popover>
    </Box>
  );
};
